import { Stack, Tooltip, Typography } from '@mui/material';
import { AnalyticsEventName, trackAmplitudeEvent } from '@schooly/analytics';
import { Assessment, usePerformAssessmentActionMutation } from '@schooly/api';
import { ApiError, AssessmentAction, ReportStatuses } from '@schooly/api';
import { HeaderActions } from '@schooly/components/header-actions';
import { NotificationActions, useNotifications } from '@schooly/components/notifications';
import { DATE_FORMAT } from '@schooly/constants';
import { PublishIcon, ReportGeneratedIcon, SimpleButton, UnpublishIcon } from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import React, { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import AssessmentStatuses from '../../../constants/assessmentStatuses';
import { useLastRefreshTimeContext } from '../../../context/LastRefreshTimeContext';
import { useRouter } from '../../../context/router/useRouter';
import useAppLocation from '../../../hooks/useAppLocation';
import useRequestWithProgress from '../../../hooks/useRequestWithProgress';

interface AssessmentsPreviewActionsProps {
  assessment: Assessment;
  isIncomplete?: boolean;
  onUpdate: () => Promise<any>;
}

export const AssessmentsPreviewActions: FC<AssessmentsPreviewActionsProps> = ({
  assessment,
  isIncomplete,
  onUpdate,
}) => {
  const navigate = useNavigate();
  const location = useAppLocation();
  const { showNotification } = useNotifications();
  const { id } = useParams<'id'>();
  const { closeAndClean } = useRouter();
  const { updateLastRefreshTime } = useLastRefreshTimeContext();
  const { formatMessage } = useIntl();

  const performAssessmentActionMutation = usePerformAssessmentActionMutation();

  const isAssessmentPublished = assessment.status === AssessmentStatuses.Published;
  const isReportPublished = assessment.reports.some(
    (r) => r.report_status === ReportStatuses.Published,
  );
  const hideButton = isAssessmentPublished && isReportPublished;

  const [title, tooltipTitle] = useMemo(() => {
    if (isIncomplete) {
      return [
        formatMessage({ id: 'status-Draft' }),
        formatMessage({ id: 'assessments-IncompleteHint' }),
      ];
    }

    if (isAssessmentPublished) {
      return [
        `${formatMessage({ id: 'status-Published' })}${' '} ${
          assessment.publish_date
            ? format(newDateTimezoneOffset(assessment.publish_date), DATE_FORMAT)
            : ''
        }`,
        undefined,
      ];
    }

    return [formatMessage({ id: 'status-NotPublished' }), undefined];
  }, [formatMessage, assessment.publish_date, isAssessmentPublished, isIncomplete]);

  const performAction = useCallback(
    async (action: AssessmentAction) => {
      if (!id) return;

      const notificationActions: NotificationActions | undefined =
        action === AssessmentAction.Publish
          ? [
              {
                textId: 'assessments-ViewAssessment',
                handler: () =>
                  navigate(`/assessments/${id}`, {
                    state: location.state,
                  }),
                buttonColor: 'light',
              },
              {
                textId: 'assessments-Unpublish',
                handler: async () => {
                  try {
                    await performAssessmentActionMutation.mutateAsync({
                      id,
                      action: AssessmentAction.Unpublish,
                    });

                    await onUpdate();
                    navigate(`/assessments/${id}`, {
                      state: location.state,
                    });
                  } catch (error) {
                    showNotification({
                      message: (error as ApiError).reason,
                      type: 'error',
                    });
                  }
                },
                isOutline: true,
                buttonColor: 'white-text',
              },
            ]
          : undefined;

      const response = await performAssessmentActionMutation.mutateAsync({
        id,
        action,
      });

      await onUpdate();

      trackAmplitudeEvent({
        name: AnalyticsEventName.AssessmentStatusChange,
        payload: {
          assessment_id: id,
          new_status: formatMessage({
            id: isAssessmentPublished ? 'assessments-Unpublish' : 'assessments-Publish',
          }),
        },
      });

      showNotification({
        message: response.success,
        type: 'success',
        actions: notificationActions,
      });

      updateLastRefreshTime();

      if (action === AssessmentAction.Publish) {
        closeAndClean();
      }
    },
    [
      id,
      performAssessmentActionMutation,
      onUpdate,
      formatMessage,
      isAssessmentPublished,
      showNotification,
      updateLastRefreshTime,
      navigate,
      location.state,
      closeAndClean,
    ],
  );

  const [publishAssessment, isPublishing] = useRequestWithProgress(performAction);

  const handleClick = useCallback(async () => {
    const action = isAssessmentPublished ? AssessmentAction.Unpublish : AssessmentAction.Publish;
    await publishAssessment(action);
    return true;
  }, [isAssessmentPublished, publishAssessment]);

  const getStyleType = () => {
    if (isIncomplete) return;
    if (isAssessmentPublished) return 'success';
    return 'warning';
  };

  const actions = useMemo(() => {
    return [
      {
        titleTextId: formatMessage({
          id: isAssessmentPublished ? 'assessments-Unpublish' : 'assessments-Publish',
        }),
        handler: !assessment.autogenerated ? handleClick : undefined,
        icon: isAssessmentPublished ? (
          <UnpublishIcon />
        ) : (
          <PublishIcon className="reset-svg-currentColor" />
        ),
        pendingTitle: formatMessage({ id: isAssessmentPublished ? 'unPublishing' : 'publishing' }),
      },
    ];
  }, [formatMessage, handleClick, isAssessmentPublished, assessment.autogenerated]);

  const headerActions = (
    <HeaderActions
      title={title ?? ''}
      tooltipTitle={tooltipTitle}
      actions={actions}
      styleType={getStyleType()}
      pending={isPublishing}
      disabled={!assessment.can_be_edited || assessment.autogenerated || isIncomplete || hideButton}
    />
  );

  return assessment.autogenerated ? (
    <Tooltip
      title={
        <Stack alignItems="flex-start" gap={1}>
          <Typography>{formatMessage({ id: 'assessments-AutoGeneratedAction' })}</Typography>
          <SimpleButton
            onClick={() => {
              navigate(`/reports/${assessment.generated_from_report_id}`);
            }}
            sx={(theme) => ({
              '.svg-icon': {
                width: theme.spacing(2),
                height: theme.spacing(2),
              },
              fontSize: theme.spacing(1.5),
            })}
            startIcon={<ReportGeneratedIcon />}
          >
            {formatMessage({ id: 'assessments-GoToReport' })}
          </SimpleButton>
        </Stack>
      }
    >
      <Stack>{headerActions}</Stack>
    </Tooltip>
  ) : (
    headerActions
  );
};
